<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  UiButton, UiIcon, UiText, UiAside, UiDivider,
  UiToggleGroup, UiToggleButton, UiSelect, UiMultiSelectChips,
} from '@shopexperts/shared';
import type { AdminExpertFilterOptions } from '@shopexperts/legacy-api';
import {
  countActiveExpertFilters,
  createEmptyExpertFilters,
  expertFiltersEqual,
  formatFilterLabel,
  resolveCountryName,
  resolveLanguageName,
  type ExpertManagerFilterState,
} from '@/app/composables/expert-manager/expertManagerFilters';

const props = defineProps<{
  filterOptions: AdminExpertFilterOptions;
  tagOptions: { value: string; label: string }[];
  tagsLoading?: boolean;
}>();

const filters = defineModel<ExpertManagerFilterState>({ required: true });

const emit = defineEmits<{
  savePreset: [];
}>();

const isOpen = ref(false);

/**
 * The panel edits a draft and commits once, on close. Writing straight to the
 * model would refetch the list and re-render the page underneath on every
 * toggle click — expensive, visually noisy, and it fights the open panel.
 */
const draft = ref<ExpertManagerFilterState>({ ...filters.value });

/** While open the panel shows its own draft; closed, it reflects what is applied. */
const shown = computed(() => (isOpen.value ? draft.value : filters.value));

function commit() {
  // `search` lives outside the panel, so the committed value always wins.
  const next = { ...draft.value, search: filters.value.search };
  if (!expertFiltersEqual(next, filters.value)) filters.value = next;
}

watch(isOpen, (open) => {
  if (open) draft.value = { ...filters.value };
  else commit();
});

function set<K extends keyof ExpertManagerFilterState>(key: K, value: ExpertManagerFilterState[K]) {
  draft.value = { ...draft.value, [key]: value };
}

function field<K extends keyof ExpertManagerFilterState>(key: K) {
  return computed({
    get: () => draft.value[key],
    set: (value) => set(key, value),
  });
}

const status = field('status');
const expertType = field('expertType');
const country = field('country');
const serviceCategory = field('serviceCategory');
const partnerTier = field('partnerTier');
const proStatus = field('proStatus');
const language = field('language');
const claimStatus = field('claimStatus');

// UiMultiSelectChips speaks strings; the API matches tags by numeric ID.
function tagIdField(key: 'tagIds' | 'excludedTagIds') {
  return computed<string[]>({
    get: () => draft.value[key].map(String),
    set: (values) => set(key, values.map(Number).filter((id) => Number.isInteger(id) && id > 0)),
  });
}

const selectedTagIds = tagIdField('tagIds');
const excludedTagIds = tagIdField('excludedTagIds');

// A tag can't be both required and excluded — that view is always empty.
const includeTagOptions = computed(() =>
  props.tagOptions.filter((option) => !excludedTagIds.value.includes(option.value)),
);
const excludeTagOptions = computed(() =>
  props.tagOptions.filter((option) => !selectedTagIds.value.includes(option.value)),
);

const activeFilterCount = computed(() => countActiveExpertFilters(shown.value));
const hasActiveFilters = computed(() => activeFilterCount.value > 0);

function toSelectOptions(items: string[], labelFormatter?: (v: string) => string) {
  return items.map((item) => ({
    value: item,
    label: labelFormatter ? labelFormatter(item) : formatFilterLabel(item),
  }));
}

function clearAll() {
  // Keep the search box — it lives outside this panel.
  draft.value = { ...createEmptyExpertFilters(), search: draft.value.search };
}

/** Commit before handing off, so the dialog stores what the panel is showing. */
function requestSavePreset() {
  commit();
  isOpen.value = false;
  emit('savePreset');
}

const statusColorMap: Record<string, string> = {
  published: 'text-emerald-700',
  application: 'text-amber-700',
  draft: 'text-gray-500',
  unpublished: 'text-rose-600',
  banned: 'text-gray-700',
};

const countryOptions = computed(() =>
  toSelectOptions(props.filterOptions.countries, (v) => resolveCountryName(v))
);
const serviceCategoryOptions = computed(() => toSelectOptions(props.filterOptions.service_categories, (v) => v));
const languageOptions = computed(() =>
  toSelectOptions(props.filterOptions.languages, (v) => resolveLanguageName(v))
);
const claimStatusOptions = computed(() =>
  props.filterOptions.claim_statuses.length
    ? props.filterOptions.claim_statuses
    : (['claimed', 'unclaimed'] as const)
);
</script>

<template>
  <!-- Filter button only — active filter chips are rendered by the parent -->
  <!-- UiButton only renders its named slots, so the caption goes through `label`. -->
  <UiButton
    variant="outline"
    size="md"
    label="Filters"
    @click="isOpen = true"
  >
    <template #before_label>
      <UiIcon name="filter" class="w-4 h-4" />
    </template>
    <template v-if="activeFilterCount > 0" #after_label>
      <span class="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-se-primary text-xs font-semibold text-se-dark">
        {{ activeFilterCount }}
      </span>
    </template>
  </UiButton>

  <!-- Filter Aside Panel -->
  <UiAside v-model="isOpen" position="right">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-100">
        <div>
          <UiText as="h2" variant="h3" text="Filters" class="text-gray-900" />
          <UiText
            v-if="hasActiveFilters"
            as="p"
            variant="caption-normal"
            class="text-gray-400 mt-0.5"
          >
            {{ activeFilterCount }} active {{ activeFilterCount === 1 ? 'filter' : 'filters' }}
          </UiText>
        </div>
        <button class="p-1.5 rounded-md hover:bg-gray-100 transition-colors" @click="isOpen = false">
          <UiIcon name="close" class="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <!-- Filter sections -->
      <div class="flex-1 overflow-y-auto p-6 space-y-7">
        <!-- Tags — the primary internal segmentation, so it leads -->
        <div>
          <div class="flex items-baseline justify-between mb-3">
            <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block">
              Tags
            </UiText>
            <UiText v-if="draft.tagIds.length > 1" as="span" variant="caption-normal" class="text-gray-400">
              matches all selected
            </UiText>
          </div>
          <UiMultiSelectChips
            v-model="selectedTagIds"
            :options="includeTagOptions"
            :max="25"
            placeholder="Search tags..."
            size="md"
            open-on-click
            show-chevron
          />
          <UiText
            v-if="!tagsLoading && tagOptions.length === 0"
            as="p"
            variant="caption-normal"
            class="text-gray-400 mt-2"
          >
            No tags yet — add tags from an expert's profile.
          </UiText>
        </div>

        <div>
          <div class="flex items-baseline justify-between mb-3">
            <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block">
              Exclude tags
            </UiText>
            <UiText v-if="draft.excludedTagIds.length > 0" as="span" variant="caption-normal" class="text-gray-400">
              hides experts with any of these
            </UiText>
          </div>
          <UiMultiSelectChips
            v-model="excludedTagIds"
            :options="excludeTagOptions"
            :max="25"
            placeholder="Search tags..."
            size="md"
            open-on-click
            show-chevron
          />
        </div>

        <UiDivider color="light" />

        <!-- Status — toggle buttons with color accents -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Status
          </UiText>
          <UiToggleGroup v-model="status">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="s in filterOptions.statuses"
                :key="s"
                :value="s"
                variant="outline"
              >
                <template #before_label>
                  <span
                    class="w-2 h-2 rounded-full shrink-0"
                    :class="{
                      'bg-emerald-400': s === 'published',
                      'bg-amber-400': s === 'application',
                      'bg-gray-300': s === 'draft',
                      'bg-rose-400': s === 'unpublished',
                      'bg-gray-700': s === 'banned',
                    }"
                  />
                </template>
                <template #label>
                  <span :class="statusColorMap[s] || 'text-gray-600'">{{ formatFilterLabel(s) }}</span>
                </template>
              </UiToggleButton>
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Expert Type — toggle buttons -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Expert Type
          </UiText>
          <UiToggleGroup v-model="expertType">
            <div class="flex gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="t in filterOptions.expert_types"
                :key="t"
                :value="t"
                :label="formatFilterLabel(t)"
                variant="outline"
              />
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Claim Status — toggle buttons -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Claim Status
          </UiText>
          <UiToggleGroup v-model="claimStatus">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="c in claimStatusOptions"
                :key="c"
                :value="c"
                :label="formatFilterLabel(c)"
                variant="outline"
              />
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Partner Tier — toggle buttons -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Partner Tier
          </UiText>
          <UiToggleGroup v-model="partnerTier">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="tier in filterOptions.partner_tiers"
                :key="tier"
                :value="tier"
                :label="formatFilterLabel(tier)"
                variant="outline"
              />
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Pro Status — toggle buttons -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Membership
          </UiText>
          <UiToggleGroup v-model="proStatus">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton value="true" label="Pro" variant="outline" />
              <UiToggleButton value="false" label="Non-pro" variant="outline" />
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Country — searchable select -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Country
          </UiText>
          <UiSelect
            v-model="country"
            :options="[{ value: '', label: 'All countries' }, ...countryOptions]"
            placeholder="All countries"
            size="md"
          />
        </div>

        <UiDivider color="light" />

        <!-- Language — searchable select -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Language
          </UiText>
          <UiSelect
            v-model="language"
            :options="[{ value: '', label: 'All languages' }, ...languageOptions]"
            placeholder="All languages"
            size="md"
          />
        </div>

        <UiDivider color="light" />

        <!-- Service Category — searchable select -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3">
            Service Category
          </UiText>
          <UiSelect
            v-model="serviceCategory"
            :options="[{ value: '', label: 'All services' }, ...serviceCategoryOptions]"
            placeholder="All services"
            size="md"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="p-6 border-t border-gray-100 space-y-3">
        <UiButton
          variant="outline"
          size="md"
          class="w-full"
          label="Save as tab"
          :disabled="!hasActiveFilters && !draft.search"
          @click="requestSavePreset"
        >
          <template #before_label>
            <UiIcon name="plus" class="w-3.5 h-3.5" />
          </template>
        </UiButton>
        <div class="flex items-center justify-between gap-3">
          <UiButton
            v-if="hasActiveFilters"
            variant="outline"
            size="md"
            label="Clear all"
            @click="clearAll"
          >
            <template #before_label>
              <UiIcon name="close" class="w-3.5 h-3.5" />
            </template>
          </UiButton>
          <div v-else />
          <UiButton
            variant="primary"
            size="md"
            label="Done"
            @click="isOpen = false"
          />
        </div>
      </div>
    </div>
  </UiAside>
</template>
